import React, { useEffect } from 'react';
import {
    BrowserRouter,
    Routes,
    Route,
    useLocation
} from "react-router-dom";

import PageTemplate from "./PageTemplate.js";
import Home from "./Home.js";
import About from "./About.js";
import Services from "./Services.js";
import Contact from "./Contact.js";
import Guides from "./Guides.js";
import Lawyers from "./Lawyers.js";
import L500Lawyers from "./L500Lawyers.js";
import L500Firms from "./L500Firms.js";
import Firms from "./Firms.js";
import Feedback from "./Feedback.js";
import Research from "./Research.js";
import ResearchIndex from "./ResearchIndex.js";
import Editorial from "./Editorial.js";
import EditorialFirms from "./EditorialFirms.js";
import EditorialLawyers from "./EditorialLawyers.js";

import { trackPageView } from './analytics';

import 'bootstrap/dist/css/bootstrap.min.css';
import "./css/style.css";

const SITE_URL = "https://lcmillsconsulting.com";
const DEFAULT_DESCRIPTION = "L C Mills Consulting helps law firms improve legal directory performance through submission strategy, best-practice training, and ranking analysis.";
const PAGE_DESCRIPTIONS = {
    "/about": "Laura Mills, former editor of the Chambers USA Guide, advises law firms on directory submissions and ranking performance.",
    "/services": "Best practice seminars, submission writing, submission deep-dives, Chambers Unpublished report analysis, and copyediting.",
    "/contact": "Contact L C Mills Consulting about submission support, best practice training, or ranking analysis for your firm.",
};
// Private sections are noindexed client-side; crawlers reading the initial HTML still see index,follow.
const PRIVATE_PATH_PREFIXES = ["/guides", "/firms", "/lawyers", "/l500", "/feedback", "/research", "/editorial"];

function AnalyticsTracker() {
    const location = useLocation();

    useEffect(() => {
        // Track page view on route change
        const pageTitle = document.title;
        trackPageView(location.pathname, pageTitle);
    }, [location]);
}

function setMeta(attribute, key, content) {
    let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
    if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attribute, key);
        document.head.appendChild(tag);
    }
    tag.setAttribute("content", content);
}

function setCanonical(href) {
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
        link = document.createElement("link");
        link.rel = "canonical";
        document.head.appendChild(link);
    }
    link.href = href;
}

function SeoTracker() {
    const { pathname } = useLocation();

    useEffect(() => {
        const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
        const isPrivate = PRIVATE_PATH_PREFIXES.some(
            (prefix) => path === prefix || path.startsWith(`${prefix}/`)
        );
        const url = `${SITE_URL}${path}`;

        setMeta("name", "robots", isPrivate ? "noindex, nofollow" : "index, follow");
        setMeta("name", "description", PAGE_DESCRIPTIONS[path] || DEFAULT_DESCRIPTION);
        setMeta("property", "og:url", url);
        setCanonical(url);
    }, [pathname]);

    return null;
}

export default function App() {
    return (
        <BrowserRouter>
            <AnalyticsTracker />
            <SeoTracker />
            <Routes>
                <Route path="/" element={<PageTemplate pageContent={<Home />} />} />
                <Route path="/about" element={<PageTemplate pageContent={<About />} />} />
                <Route path="/services" element={<PageTemplate pageContent={<Services />} />} />
                <Route path="/contact" element={<PageTemplate pageContent={<Contact />} />} />
                <Route path="/guides" element={<PageTemplate pageContent={<Guides />} />} />
                <Route path="/firms" element={<Firms />} />
                <Route path="/firms/:guideId" element={<Firms />} />
                <Route path="/lawyers" element={<Lawyers />} />
                <Route path="/lawyers/:guideId" element={<Lawyers />} />
                <Route path="/l500/firms" element={<L500Firms />} />
                <Route path="/l500/firms/:guideId" element={<L500Firms />} />
                <Route path="/l500/lawyers" element={<L500Lawyers />} />
                <Route path="/l500/lawyers/:guideId" element={<L500Lawyers />} />
                <Route path="/feedback" element={<PageTemplate pageContent={<Feedback />} />} />
                <Route path="/research" element={<PageTemplate pageContent={<ResearchIndex />} />} />
                <Route path="/research/:guideId/:year?" element={<Research />} />
                <Route path="/editorial" element={<Editorial />} />
                <Route path="/editorial/firms/:firmId" element={<EditorialFirms />} />
                <Route path="/editorial/lawyers/:lawyerId" element={<EditorialLawyers />} />
            </Routes>
        </BrowserRouter>
    );
}